/**
 * Payment API service for VietQR/SePay integration
 *
 * This service handles communication with Modal backend payment endpoints
 */

// Get Modal API URL from environment variables
const MODAL_API_URL = import.meta.env.VITE_MODAL_API_BASE_URL || '';

if (!MODAL_API_URL) {
  console.warn('[PaymentAPI] VITE_MODAL_API_BASE_URL not set in environment variables');
}

export interface CreateOrderRequest {
  amount: number;
  admin_user_id: string;
}

export interface CreateOrderResponse {
  success: boolean;
  order: {
    orderId: string;
    amount: number;
    status: 'pending' | 'completed' | 'failed' | 'expired';
    description: string;
    qrData: {
      qrUrl: string;
      accountNo: string;
      bankName: string;
      accountName: string;
    };
    createdAt: string;
    createdBy: string;
    paidAt: string | null;
    transactionData: any | null;
  };
  error?: string;
}

export interface CheckStatusResponse {
  orderId: string;
  status: 'pending' | 'completed' | 'failed' | 'expired';
  amount: number;
  createdAt: string;
  paidAt: string | null;
}

export const paymentApi = {
  /**
   * Create payment order and generate QR code
   *
   * @param amount - Amount to deposit in VND
   * @param adminUserId - Admin user ID who creates the order
   * @returns Order data with QR code URL
   */
  createOrder: async (
    amount: number,
    adminUserId: string
  ): Promise<CreateOrderResponse> => {
    try {
      const response = await fetch(`${MODAL_API_URL}/admin/payment/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount,
          admin_user_id: adminUserId
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data: CreateOrderResponse = await response.json();
      return data;
    } catch (error) {
      console.error('[PaymentAPI] Create order failed:', error);
      throw error;
    }
  },

  /**
   * Check payment order status (for polling)
   *
   * @param orderId - Order ID to check
   * @returns Order status and details
   */
  checkStatus: async (orderId: string): Promise<CheckStatusResponse> => {
    try {
      const response = await fetch(`${MODAL_API_URL}/payment/status/${orderId}`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data: CheckStatusResponse = await response.json();
      return data;
    } catch (error) {
      console.error('[PaymentAPI] Check status failed:', error);
      throw error;
    }
  },

  /**
   * Poll payment status until completion or timeout
   *
   * @param orderId - Order ID to poll
   * @param onUpdate - Callback when status changes
   * @param intervalMs - Polling interval in milliseconds (default: 3000)
   * @returns Interval ID for cleanup
   */
  pollStatus: (
    orderId: string,
    onUpdate: (status: 'pending' | 'completed' | 'failed' | 'expired') => void,
    intervalMs: number = 3000
  ): NodeJS.Timeout => {
    let lastStatus: string | null = null;

    const interval = setInterval(async () => {
      try {
        const data = await paymentApi.checkStatus(orderId);

        // Only call onUpdate if status changed
        if (data.status !== lastStatus) {
          lastStatus = data.status;
          onUpdate(data.status);
        }

        // Stop polling for terminal statuses
        if (['completed', 'expired', 'failed'].includes(data.status)) {
          clearInterval(interval);
        }
      } catch (error) {
        console.error('[PaymentAPI] Poll error:', error);
      }
    }, intervalMs);

    return interval;
  }
};

export default paymentApi;
